import { createRouter, createWebHistory } from 'vue-router'
import { setupListStateNavigation } from '@/utils/useListState'
import { hasFocusTarget, syncNavState } from './navState'
import { useUserStore, useDataDetailCacheStore, useProjectDetailCacheStore, useSessionDetailCacheStore } from '@/stores'
import { roleMenuMapping } from '@/data/role_menu_mapping'
import { MENU_ID, ERROR_TYPE } from '@/constants/consts'
import { API_URL } from '@/constants/api'
import apiHelper from '@/utils/apiHelper'
import { $dialog } from '@/plugins/dialog-alert'
import { $loading } from '@/plugins/simple-loading'
import type { UserRole } from '@/types'
import type { ApiResponse } from '@/types/api'
import type { DataDetailResponse } from '@/types/data'
// 로그인 화면은 지연 로딩하지 않는다. 로그아웃·세션 만료로 /login 에 갈 때 이 화면 코드를 그제서야 받으면
// 받는 동안 이동이 끝나지 않아 이전 화면이 로그아웃된 채 남고, 받기에 실패하면 이동이 아예 멈춘다.
import LoginPage from '@/views/auth/LoginPage.vue'

// 접근 제한 경고 모달을 띄우고 사용자가 확인/취소(ESC, backdrop 포함)로 닫을 때까지 대기한다.
// beforeEach 가드가 async 이므로, 모달이 닫힌 뒤에야 네비게이션 취소/리다이렉트를 진행하도록 Promise 로 감싼다.
const confirmAccessDeniedDialog = (title: string, msg: string): Promise<void> => {
  return new Promise((resolve) => {
    // warn() 이 이전 콜백을 초기화하므로 콜백은 반드시 띄운 뒤에 등록한다.
    $dialog.warn(title, msg)
    $dialog.callback(() => resolve())
    $dialog.cancel(() => resolve())
  })
}

// 상세 화면 권한 확인 조회. 응답을 기다리는 동안 화면이 바뀌지 않으므로 로딩을 띄운다.
const getWithLoading = async <T = unknown>(url: string) => {
  $loading.show()
  try {
    return await apiHelper.get<T>(url)
  } finally {
    $loading.hide()
  }
}

// DOI 해석 응답의 url(attributes.url)은 데이터 상세 페이지의 절대 주소일 수 있으므로, 화면 이동에는 경로만 쓴다.
const toRoutePath = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.pathname + parsed.search + parsed.hash
  } catch {
    return url
  }
}

// DOI 미발급 안내 화면 경로. DOI 접미사의 '/' 는 경로 구분자로 두고 구간마다 인코딩한다.
const unissuedDoiPath = (doi: string): string =>
  `/unissued-doi/${doi.split('/').map(encodeURIComponent).join('/')}`

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    // focus 작업이 있어 스크롤이 자동으로 이동하는 경우 예외 처리
    if (hasFocusTarget() || to.meta?.preserveScroll) {
      return false
    }
    return { top: 0, left: 0 }
  },
  routes: [
    // Auth
    {
      path: '/login',
      name: 'LOGIN',
      component: LoginPage,
      meta: { public: true },
    },

    // User Role Pages
    {
      path: '/',
      name: 'HOME',
      component: () => import('@/views/home/Home.vue'),
      meta: { menuId: MENU_ID.HOME },
    },
    {
      path: '/projects',
      name: 'PROJECTS',
      component: () => import('@/views/projects/Projects.vue'),
      meta: { menuId: MENU_ID.PROJECT },
    },
    {
      path: '/project/:projectId',
      alias: '/projects/:projectId',
      name: 'PROJECT',
      component: () => import('@/views/projects/Project.vue'),
      meta: { menuId: MENU_ID.PROJECT },
    },
    {
      path: '/project/:projectId/session/:sessionId',
      name: 'SESSION',
      component: () => import('@/views/projects/ProjectSession.vue'),
      meta: { menuId: MENU_ID.PROJECT },
    },
    {
      path: '/data/:dataId',
      alias: ['/projects/data/:dataId', '/project/data/:dataId'],
      name: 'DATA_SHORT',
      component: () => import('@/views/projects/ProjectSessionData.vue'),
    },
    {
      path: '/project/:projectId/session/:sessionId/data/:dataId',
      name: 'DATA',
      component: () => import('@/views/projects/ProjectSessionData.vue'),
    },
    {
      // DOI 는 경로로 받는다: /unissued-doi/10.22747/tem.839 (서버 DOI 리졸버의 미발급 리다이렉트도 같은 경로)
      path: '/unissued-doi/:doi(.*)?',
      alias: '/doi-unissued/:doi(.*)?',
      name: 'DOI_UNISSUED',
      component: () => import('@/views/doi/DoiUnissued.vue'),
    },
    {
      path: '/10.22747/:doiPath(.*)',
      name: 'DOI_PREFIX_RESOLVE',
      component: () => import('@/views/home/Home.vue'),
      beforeEnter: async (to, _from, next) => {
        const rawDoi = to.params.doiPath
        const suffix = Array.isArray(rawDoi) ? rawDoi.join('/') : (rawDoi || '')
        const fullDoi = `10.22747/${suffix}`
        try {
          const res = await fetch(`/api/10.22747/${suffix}`, {
            headers: { Accept: 'application/json' },
          })
          if (res.ok) {
            const json = await res.json()
            if (json?.attributes?.url) {
              next(toRoutePath(json.attributes.url))
              return
            }
          }
        } catch (e) {
          console.error('Failed to resolve DOI client-side:', e)
        }
        next(unissuedDoiPath(fullDoi))
      },
    },
    {
      path: '/doi/:doiPath(.*)',
      name: 'DOI_RESOLVE',
      component: () => import('@/views/home/Home.vue'),
      beforeEnter: async (to, _from, next) => {
        const rawDoi = to.params.doiPath
        const cleanDoi = Array.isArray(rawDoi) ? rawDoi.join('/') : (rawDoi || '')
        try {
          const res = await fetch(`/api/doi/${cleanDoi}`, {
            headers: { Accept: 'application/json' },
          })
          if (res.ok) {
            const json = await res.json()
            if (json?.attributes?.url) {
              next(toRoutePath(json.attributes.url))
              return
            }
          }
        } catch (e) {
          console.error('Failed to resolve DOI client-side:', e)
        }
        next(unissuedDoiPath(cleanDoi))
      },
    },

    // Explorer
    {
      path: '/explorer',
      name: 'EXPLORER',
      component: () => import('@/views/explorer/Explorer.vue'),
      meta: { menuId: MENU_ID.EXPLORER },
    },

    // Researcher / PM Role Pages
    {
      path: '/dashboard',
      name: 'DASHBOARD',
      component: () => import('@/views/dashboard/Dashboard.vue'),
      meta: { menuId: MENU_ID.DASHBOARD },
    },
    {
      path: '/workspace',
      name: 'WORKSPACE',
      component: () => import('@/views/workspace/Workspace.vue'),
      meta: { menuId: MENU_ID.WORKSPACE },
    },

    // Admin Role Pages
    {
      path: '/monitoring',
      name: 'MONITORING',
      component: () => import('@/views/monitoring/Monitoring.vue'),
      meta: { menuId: MENU_ID.MONITORING },
    },
    {
      path: '/management',
      name: 'MANAGEMENT',
      component: () => import('@/views/management/Management.vue'),
      meta: { menuId: MENU_ID.MANAGEMENT },
    },
    {
      path: '/management/doi/review/:requestNo',
      name: 'DOI_REVIEW',
      component: () => import('@/views/management/DoiReviewView.vue'),
      meta: { menuId: MENU_ID.MANAGEMENT },
    },

    // 매치되는 라우트가 없을 때(오타, 삭제된 링크 등) — 반드시 배열 마지막에 위치해야 함
    {
      path: '/:pathMatch(.*)*',
      name: 'NOT_FOUND',
      component: () => import('@/views/NotFound.vue'),
    },
  ],
})

// 목록 화면 상태 복원: 뒤로 가기·브레드크럼 복귀 여부를 다른 가드보다 먼저 판별
setupListStateNavigation(router)

router.beforeEach(async (to, from) => {
  const userStore = useUserStore()

  // 로그인 화면으로 갈 때는 이전 화면에서 닫히지 않은 로딩을 정리
  if (to.path === '/login') $loading.reset()

  // 1. 비인증 사용자는 로그인 페이지(/login) 외에는 어떤 페이지도 접근 불가 (무조건 /login으로 강제 리다이렉트)
  if (!userStore.isAuthenticated) {
    if (to.path === '/login') {
      return true
    }
    return { path: '/login' }
  }

  // 2. 인증된 사용자가 로그인 페이지 접근 시 리다이렉트
  if (to.path === '/login') {
    const userRole = (userStore.currentUser.role || '').toUpperCase() as UserRole
    if (userRole === 'ADMIN') return { path: '/dashboard' }
    return { path: '/' }
  }

  // 4. (2026-09 정책 변경 이전) 루트(/) 접근 시 협력파트너를 무조건 워크스페이스로
  // 돌려보내던 리다이렉트는 제거했다 — 협력파트너도 이제 HOME 메뉴가 열려 있어서
  // (role_menu_mapping.ts 참고) Home을 직접 볼 수 있어야 한다. 이 블록이 남아있으면
  // 상단 메뉴에서 Home을 눌러도 이 가드가 먼저 가로채 항상 워크스페이스로 튕겨버린다.
  // 로그인 직후 기본 랜딩 페이지는 위 1번(로그인 페이지 리다이렉트)에서 별도로 처리한다.

  // 권한 체크 (RBAC)
  const userRole = (userStore.currentUser.role || '').toUpperCase() as UserRole
  const allowedMenus = (userRole && roleMenuMapping[userRole]) ? roleMenuMapping[userRole] : []
  const requiredMenuId = to.meta.menuId as string

  // 권한이 필요한 메뉴인데 허용 목록에 없는 경우
  if (requiredMenuId && !allowedMenus.includes(requiredMenuId)) {
    if (to.path === '/') return true

    await confirmAccessDeniedDialog('접근 제한', '해당 페이지에 대한 접근 권한이 없습니다.')

    if (from.name) {
      return false
    } else {
      // 2026-09: 협력파트너도 다른 역할과 동일하게 홈('/')으로 리다이렉트한다.
      return { path: '/', replace: true }
    }
  }

  // 5. 프로젝트 상세 페이지(PROJECT) 접근 시 연구원/협력파트너의 프로젝트 멤버십 비동기 검사.
  // Explorer 프로젝트 카드 클릭 등, 이 가드를 거치지 않고 접근 제한 모달을 직접 띄우는
  // 곳(Explorer.vue)도 있지만, 여기서는 URL 직접 접속/뒤로가기 등 모든 진입 경로를 막는다.
  if (to.name === 'PROJECT' && (userRole === 'RESEARCHER' || userRole === 'COOPERATION_PARTNER')) {
    const projectId = Number(to.params.projectId)
    if (projectId && !isNaN(projectId)) {
      const res = (await getWithLoading(API_URL.PROJECT.DETAIL(projectId))) as ApiResponse<unknown> | undefined
      const errorCode = res?.errorCode
      const isDenied = !res || errorCode === ERROR_TYPE.NO_PERMISSION.errorCode

      if (isDenied) {
        await confirmAccessDeniedDialog('접근 제한', '참여하지 않은 프로젝트에는 접근할 수 없습니다.')
        // 2026-09: 협력파트너도 다른 역할과 동일하게 홈('/')으로 리다이렉트한다.
        return from.name ? false : { path: '/' }
      }

      // 권한 체크와 함께 이미 받아온 상세를 캐싱해서, 프로젝트 화면이 같은 API를 다시 호출하지 않도록 한다.
      if (errorCode === 0 && res?.data) {
        useProjectDetailCacheStore().set(projectId, res.data as Record<string, unknown>)
      }
    }
  }

  // 6. 세션 상세 페이지(SESSION) 접근 시 연구원/협력파트너의 프로젝트 멤버십 비동기 검사
  if (to.name === 'SESSION' && (userRole === 'RESEARCHER' || userRole === 'COOPERATION_PARTNER')) {
    const sessionId = Number(to.params.sessionId)
    if (sessionId && !isNaN(sessionId)) {
      const res = (await getWithLoading(API_URL.SESSION.DETAIL(sessionId))) as ApiResponse<unknown> | undefined
      const errorCode = res?.errorCode
      const isDenied = !res || errorCode === ERROR_TYPE.NO_PERMISSION.errorCode

      if (isDenied) {
        await confirmAccessDeniedDialog('접근 제한', '참여하지 않은 프로젝트의 세션에는 접근할 수 없습니다.')
        // 2026-09: 협력파트너도 다른 역할과 동일하게 홈('/')으로 리다이렉트한다.
        return from.name ? false : { path: '/' }
      }

      // 권한 체크와 함께 이미 받아온 상세를 캐싱해서, 세션 화면이 같은 API를 다시 호출하지 않도록 한다.
      if (errorCode === 0 && res?.data) {
        useSessionDetailCacheStore().set(sessionId, res.data as Record<string, unknown>)
      }
    }
  }

  // 7. 데이터 상세 페이지(DATA, DATA_SHORT) 접근 시 연구원/협력파트너의 프로젝트 멤버십 비동기 검사
  if ((to.name === 'DATA' || to.name === 'DATA_SHORT') && (userRole === 'RESEARCHER' || userRole === 'COOPERATION_PARTNER')) {
    const dataId = Number(to.params.dataId)
    if (dataId && !isNaN(dataId)) {
      // apiHelper 는 요청 실패 시에도 reject 하지 않고 에러 응답(ApiResponse)을 그대로 resolve 하므로
      // try/catch 가 아니라 응답의 errorCode 로 권한 여부를 판단해야 한다.
      const res = (await getWithLoading<DataDetailResponse>(API_URL.DATA.DETAIL(dataId))) as
        | ApiResponse<DataDetailResponse>
        | undefined
      const errorCode = res?.errorCode
      const isDenied = !res || errorCode === ERROR_TYPE.NO_PERMISSION.errorCode

      if (isDenied) {
        await confirmAccessDeniedDialog('접근 제한', '참여하지 않은 프로젝트의 데이터에는 접근할 수 없습니다.')
        // 2026-09: 협력파트너도 다른 역할과 동일하게 홈('/')으로 리다이렉트한다.
        return from.name ? false : { path: '/' }
      }

      // 권한 체크와 함께 이미 받아온 상세 데이터를 캐싱해서, 페이지 컴포넌트가 같은 API를 다시 호출하지 않도록 한다.
      if (res?.data) {
        useDataDetailCacheStore().set(dataId, res.data)
      }
    }
  }

  // 유효한 접근 시 마지막 경로 저장 (리다이렉트용) — 존재하지 않는 경로(404)는 저장하지 않는다.
  if (to.name && to.name !== 'LOGIN' && to.name !== 'NOT_FOUND') {
    sessionStorage.setItem('last_valid_path', to.fullPath)
  }

  return true
})

// 이동이 끝난 history 항목의 state(탭, 검색어 등)를 화면들이 읽는 navState 에 반영
router.afterEach(() => syncNavState())

// 성공적인 탐색 후 마지막 유효 경로 저장
router.afterEach((to) => {
  if (to.path !== '/login') {
    sessionStorage.setItem('last_valid_path', to.fullPath)
  }
})

export default router
